import { Children } from 'react'
import { cn } from './internal/utils'
import { PRESET_COLORS, isPresetColor } from './internal/presets'

const STATUS_COLOR = {
  blue: 'var(--primary-color)',
  green: 'var(--success-green)',
  red: 'var(--error-color)',
  gray: 'var(--gray-500)',
}

const resolveDotColor = (color = 'blue') => {
  if (STATUS_COLOR[color]) return STATUS_COLOR[color]
  if (isPresetColor(color)) return PRESET_COLORS[color]
  return color
}

const Item = ({ children }) => children

/**
 * antd Timeline. Accepts the `items` array (v5) or `Timeline.Item` children, plus
 * `mode`, `pending` and `reverse`.
 */
const Timeline = ({
  items,
  mode = 'left',
  pending,
  pendingDot,
  reverse = false,
  className,
  style,
  children,
  ...rest
}) => {
  const resolved =
    items?.length
      ? items
      : Children.toArray(children)
          .filter(Boolean)
          .map((child, index) => ({
            key: child.key ?? index,
            color: child.props?.color,
            dot: child.props?.dot,
            label: child.props?.label,
            children: child.props?.children,
          }))

  const entries = [...resolved]
  if (pending) {
    entries.push({ key: '__pending__', dot: pendingDot, children: pending, pending: true })
  }
  if (reverse) entries.reverse()

  return (
    <ul className={cn('ui-timeline m-0 list-none p-0', `ui-timeline-${mode}`, className)} style={style} {...rest}>
      {entries.map((item, index) => {
        const isLast = index === entries.length - 1
        return (
          <li key={item.key ?? index} className="ui-timeline-item relative flex gap-3 pb-4 last:pb-0">
            <div className="relative flex w-3 shrink-0 flex-col items-center">
              {item.dot ?? (
                <span
                  className={cn(
                    'ui-timeline-item-head mt-1.5 block h-2.5 w-2.5 rounded-full border-2 bg-[var(--surface-card)]',
                    item.pending && 'animate-pulse'
                  )}
                  style={{ borderColor: resolveDotColor(item.color) }}
                />
              )}
              {!isLast ? (
                <span className="ui-timeline-item-tail absolute top-5 h-[calc(100%-0.5rem)] w-px bg-[var(--border-color)]" />
              ) : null}
            </div>
            <div className="ui-timeline-item-content min-w-0 flex-1">
              {item.label ? (
                <div className="ui-timeline-item-label text-xs text-[var(--text-muted)]">
                  {item.label}
                </div>
              ) : null}
              <div className="text-sm text-[var(--text-body)]">{item.children ?? item.content}</div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

Timeline.Item = Item

export default Timeline
